import React, { useState, useMemo } from "react";
import REGISTRY, { REGISTRY_MAP, getColumnsByCategory, DEFAULT_COLUMNS, buildLocationColumns, COLUMN_CATEGORIES } from "../constants/columnRegistry";
import "./ColumnPickerModal.css";

export default function ColumnPickerModal({ activeColumns, onApply, onClose, locations }) {
    const [selected, setSelected] = useState(() => [...activeColumns]);
    const [search, setSearch] = useState("");
    const [locOpen, setLocOpen] = useState(false);
    const [locSearch, setLocSearch] = useState("");

    const locationCols = useMemo(() => buildLocationColumns(locations), [locations]);

    const allColumnMap = useMemo(() => {
        const map = new Map(REGISTRY_MAP);
        locationCols.forEach((col) => map.set(col.key, col));
        return map;
    }, [locationCols]);

    const grouped = useMemo(() => {
        const base = getColumnsByCategory();
        if (locationCols.length > 0) {
            return { [COLUMN_CATEGORIES.LOCATION]: locationCols, ...base };
        }
        return base;
    }, [locationCols]);

    const PINNED_HEAD = ["color", "product_name"];
    const PINNED_TAIL = ["actions"];
    const PINNED_SET = useMemo(() => new Set([...PINNED_HEAD, ...PINNED_TAIL]), []);

    const reorderable = useMemo(
        () => selected.filter((k) => !PINNED_SET.has(k)),
        [selected, PINNED_SET]
    );
    const headItems = PINNED_HEAD.filter((k) => selected.includes(k));
    const hasActions = selected.includes("actions");

    const activeSet = useMemo(() => new Set(selected), [selected]);

    const activeLocCount = useMemo(
        () => locationCols.filter((c) => activeSet.has(c.key)).length,
        [locationCols, activeSet]
    );

    const filteredLocations = useMemo(() => {
        const term = locSearch.toLowerCase().trim();
        if (!term) return locationCols;
        return locationCols.filter((c) => c.label.toLowerCase().includes(term));
    }, [locationCols, locSearch]);

    const filteredGroups = useMemo(() => {
        const term = search.toLowerCase().trim();
        if (!term) return grouped;
        const result = {};
        for (const [cat, cols] of Object.entries(grouped)) {
            const filtered = cols.filter(
                (c) => c.label.toLowerCase().includes(term) || c.key.toLowerCase().includes(term)
            );
            if (filtered.length > 0) result[cat] = filtered;
        } 
        return result;
    }, [grouped, search]);

    function rebuild(middle) {
        return [...headItems, ...middle, ...(hasActions ? ["actions"] : [])];
    }

    function addColumn(key) {
        if (activeSet.has(key)) return;
        if (PINNED_SET.has(key)) {
            setSelected([...selected, key]);
            return;
        }
        setSelected(rebuild([...reorderable, key]));
    }

    function removeColumn(key) {
        const col = REGISTRY_MAP.get(key);
        if (col?.pinned) return;
        setSelected(selected.filter((k) => k !== key));
    }

    function moveUp(idx) {
        if (idx <= 0) return;
        const next = [...reorderable];
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
        setSelected(rebuild(next));
    }

    function moveDown(idx) {
        if (idx >= reorderable.length - 1) return;
        const next = [...reorderable];
        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
        setSelected(rebuild(next));
    }

    function handleReset() {
        setSelected([...DEFAULT_COLUMNS]);
    }

    function handleApply() {
        onApply(rebuild(reorderable));
        onClose();
    }

    return (
        <div className="cpm-overlay" onClick={onClose}>
            <div className="cpm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="cpm-header">
                    <h2 className="cpm-title">Customize Columns</h2>
                    <button className="cpm-close" onClick={onClose}>&times;</button>
                </div>

                <div className="cpm-body" data-tutorial="column-picker-body">
                    {/* Available columns */}
                    <div className="cpm-pane cpm-pane--available">
                        <div className="cpm-pane-header">
                            <span className="cpm-pane-title">Available</span>
                            <input
                                className="cpm-search"
                                type="text"
                                placeholder="Search columns..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="cpm-pane-list">
                            {Object.entries(filteredGroups).map(([cat, cols]) => {
                                if (cat === COLUMN_CATEGORIES.LOCATION) {
                                    return (
                                        <div key={cat} className="cpm-category cpm-loc-dropdown">
                                            <div
                                                className="cpm-loc-header"
                                                onClick={() => setLocOpen((o) => !o)}
                                            >
                                                <span className="cpm-category-label">{cat}</span>
                                                <span className="cpm-loc-meta">
                                                    {activeLocCount > 0 && (
                                                        <span className="cpm-loc-count">{activeLocCount} selected</span>
                                                    )}
                                                    <span className={`cpm-loc-chevron${locOpen ? " open" : ""}`}>&#9660;</span>
                                                </span>
                                            </div>
                                            {locOpen && (
                                                <div className="cpm-loc-body">
                                                    <input
                                                        className="cpm-loc-search"
                                                        type="text"
                                                        placeholder="Search locations..."
                                                        value={locSearch}
                                                        onChange={(e) => setLocSearch(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <div className="cpm-loc-list">
                                                        {filteredLocations.map((col) => {
                                                            const isActive = activeSet.has(col.key);
                                                            return (
                                                                <div
                                                                    key={col.key}
                                                                    className={`cpm-item ${isActive ? "cpm-item--active" : ""}`}
                                                                    onClick={() => !isActive && addColumn(col.key)}
                                                                >
                                                                    <span className="cpm-item-label">{col.label}</span>
                                                                    {!isActive && <span className="cpm-item-add">+</span>}
                                                                    {isActive && <span className="cpm-item-check">&#10003;</span>}
                                                                </div>
                                                            );
                                                        })}
                                                        {filteredLocations.length === 0 && (
                                                            <div className="cpm-loc-empty">No locations match</div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <div key={cat} className="cpm-category">
                                        <div className="cpm-category-label">{cat}</div>
                                        {cols.map((col) => {
                                            const isActive = activeSet.has(col.key);
                                            return (
                                                <div
                                                    key={col.key}
                                                    className={`cpm-item ${isActive ? "cpm-item--active" : ""}`}
                                                    onClick={() => !isActive && addColumn(col.key)}
                                                >
                                                    <span className="cpm-item-label">
                                                        {col.label || col.key}
                                                    </span>
                                                    {!isActive && <span className="cpm-item-add">+</span>}
                                                    {isActive && <span className="cpm-item-check">&#10003;</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Active columns */}
                    <div className="cpm-pane cpm-pane--active">
                        <div className="cpm-pane-header">
                            <span className="cpm-pane-title">Active ({selected.length})</span>
                        </div>

                        {headItems.length > 0 && (
                            <div className="cpm-pinned-header">
                                {headItems.map((key) => {
                                    const col = REGISTRY_MAP.get(key);
                                    return (
                                        <div key={key} className="cpm-active-item cpm-active-item--pinned cpm-active-item--top">
                                            <span className="cpm-active-item-label">
                                                {col?.label || key}
                                                <span className="cpm-pin-badge">pinned</span>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div className="cpm-pane-list">
                            {reorderable.map((key, idx) => {
                                const col = REGISTRY_MAP.get(key);
                                return (
                                    <div key={key} className="cpm-active-item">
                                        <div className="cpm-active-item-controls">
                                            <button
                                                className="cpm-arrow"
                                                onClick={() => moveUp(idx)}
                                                disabled={idx === 0}
                                            >&#9650;</button>
                                            <button
                                                className="cpm-arrow"
                                                onClick={() => moveDown(idx)}
                                                disabled={idx === reorderable.length - 1}
                                            >&#9660;</button>
                                        </div>
                                        <span className="cpm-active-item-label">
                                            {col?.label || key}
                                        </span>
                                        <button
                                            className="cpm-remove"
                                            onClick={() => removeColumn(key)}
                                        >&times;</button>
                                    </div>
                                );
                            })}
                        </div>

                        {hasActions && (
                            <div className="cpm-actions-footer">
                                <div className="cpm-active-item cpm-active-item--pinned cpm-active-item--bottom">
                                    <span className="cpm-active-item-label">
                                        Actions
                                        <span className="cpm-pin-badge">always last</span>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="cpm-footer">
                    <button className="cpm-btn cpm-btn--ghost" onClick={handleReset}>
                        Reset to Defaults
                    </button>
                    <div className="cpm-footer-right">
                        <button className="cpm-btn cpm-btn--ghost" onClick={onClose}>Cancel</button>
                        <button className="cpm-btn cpm-btn--primary" onClick={handleApply}>Apply</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
